import { signInWithCustomToken, signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "services/firebase/firebase";
import NotificationApiService from "services/notification/NotificationApiService";

const TOKEN_RETRY_ATTEMPTS = 5;
const TOKEN_RETRY_INITIAL_MS = 1500;
const TOKEN_RETRY_MAX_MS = 30000;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class FirebaseAuthService {
  constructor() {
    this._currentUser = null;
    this._authStateListener = null;
    this._authenticating = false;
    this._authPromise = null;
    this._ready = false;
    this._readyPromise = null;
    this._resolveReady = null;
  }

  async getFirebaseToken() {
    let lastError;
    let waitMs = TOKEN_RETRY_INITIAL_MS;
    for (let attempt = 1; attempt <= TOKEN_RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await NotificationApiService.post("/api/auth/firebase", {});
        const token = response.data?.data?.token || response.data?.token;
        if (token) return token;
        lastError = new Error("Token vazio");
      } catch (err) {
        lastError = err;
      }
      if (attempt < TOKEN_RETRY_ATTEMPTS) {
        await delay(Math.min(waitMs, TOKEN_RETRY_MAX_MS));
        waitMs *= 2;
      }
    }
    throw lastError;
  }

  async authenticateWithCustomToken() {
    if (!auth) {
      console.warn("Firebase Auth não inicializado (config ausente)");
      return false;
    }
    if (this._authenticating && this._authPromise) {
      return this._authPromise;
    }
    this._authenticating = true;
    this._authPromise = (async () => {
      try {
        const customToken = await this.getFirebaseToken();
        if (!customToken) {
          console.warn("Token Firebase vazio retornado pela API");
          return false;
        }
        const userCredential = await signInWithCustomToken(auth, customToken);
        this._currentUser = userCredential.user;
        this._ready = true;
        if (this._resolveReady) this._resolveReady(true);
        return true;
      } catch (error) {
        console.warn("Falha na autenticação Firebase (não crítico):", error);
        if (this._resolveReady) this._resolveReady(false);
        return false;
      } finally {
        this._authenticating = false;
        this._authPromise = null;
      }
    })();
    return this._authPromise;
  }

  setupAuthListener() {
    if (this._authStateListener || !auth) return;
    this._authStateListener = onAuthStateChanged(auth, (user) => {
      this._currentUser = user;
      if (user && !this._ready) {
        this._ready = true;
        if (this._resolveReady) this._resolveReady(true);
      }
    });
  }

  removeAuthListener() {
    if (this._authStateListener) {
      this._authStateListener();
      this._authStateListener = null;
    }
  }

  async signOutFirebase() {
    if (!auth) return;
    try {
      await signOut(auth);
      this._currentUser = null;
      this._ready = false;
      this._readyPromise = null;
      this._resolveReady = null;
    } catch (error) {
      console.warn("Erro ao fazer logout do Firebase:", error);
    }
  }

  isAuthenticated() {
    return this._currentUser !== null;
  }

  isReady() {
    return this._ready;
  }

  waitUntilReady() {
    if (this._ready) return Promise.resolve(true);
    if (!this._readyPromise) {
      this._readyPromise = new Promise((resolve) => {
        this._resolveReady = resolve;
      });
    }
    return this._readyPromise;
  }
}

export const firebaseAuthService = new FirebaseAuthService();
